// Overview operational metrics panel (#811).
//
// The one thing this component must never do is make a number up. Every value comes from
// GET /api/overview/metrics, computed over the whole tenant in the business's own time zone;
// nothing is derived from the request list this screen already has, because that list is a page
// and a page is not a period. When the server cannot answer honestly the panel renders an absent
// value rather than a confident zero.
import React from "react";
import { cockpit } from "@/api";
import { cachedRead } from "@/api/read-cache";
import type { OverviewMetrics, OverviewPeriod } from "@/api";
import { Input } from "@/ds";
import { Icon } from "@/lib";
import { useLiveInvalidation, useLiveScopeKey } from "@/live/live-provider";
import { useLang, useT } from "../i18n";
import {
  averageReading,
  countReading,
  EMPTY_OVERVIEW_RANGE,
  overviewMetricsQuery,
  overviewPanelState,
  overviewRangeProblem,
  periodRangeLabel,
  ratioReading,
  OVERVIEW_DEFAULT_PERIOD,
  OVERVIEW_PERIODS,
  type MetricReading,
  type OverviewRangeProblem,
  type OverviewRangeState,
} from "./overview-metrics.logic";

function MetricRow({
  id,
  icon,
  label,
  reading,
  hint,
}: {
  id: string;
  icon: "mail" | "clock" | "alert" | "users";
  label: string;
  reading: MetricReading;
  hint: string;
}) {
  const descriptionId = `overview-metric-${id}-hint`;
  return (
    <div className={`ov-metric ov-metric--${id}`} data-testid={`overview-metric-${id}`}>
      <span className="ov-metric__icon" data-icon={icon} aria-hidden="true">
        <Icon name={icon} size={16} />
      </span>
      <span className="ov-metric__copy">
        <span className="ov-metric__label">{label}</span>
        <span id={descriptionId} className="ov-metric__hint">{hint}</span>
      </span>
      <span
        className={`ov-metric__value${reading.kind === "value" ? "" : " ov-metric__value--absent"}`}
        data-metric-state={reading.kind}
        aria-describedby={descriptionId}
      >
        {reading.kind === "value" ? reading.text : "—"}
      </span>
    </div>
  );
}

function metricHint(
  reading: MetricReading,
  loading: string,
  available: string,
  unavailable: string,
): string {
  if (reading.kind === "loading") return loading;
  return reading.kind === "value" ? available : unavailable;
}

function metricsKey(scopeKey: string, query: NonNullable<ReturnType<typeof overviewMetricsQuery>>): string {
  return `${scopeKey}:${query.period}:${query.from ?? ""}:${query.to ?? ""}`;
}

export function OverviewMetricsPanel() {
  const t = useT();
  const { lang } = useLang();
  const scopeKey = useLiveScopeKey();
  // New requests move the requests domain; extraction and accepted fact edits move workspace. A
  // completed mailbox check moves its own domain (#2269), which still refreshes the period here.
  const invalidations = useLiveInvalidation("requests", "workspace", "mailbox");
  const [period, setPeriod] = React.useState<OverviewPeriod>(OVERVIEW_DEFAULT_PERIOD);
  const [range, setRange] = React.useState<OverviewRangeState>(EMPTY_OVERVIEW_RANGE);
  // #1536: the default period's numbers from the last visit stay on screen while they refresh.
  const [answer, setAnswer] = React.useState<{ key: string; metrics: OverviewMetrics } | null>(() => {
    const initialQuery = overviewMetricsQuery(OVERVIEW_DEFAULT_PERIOD, EMPTY_OVERVIEW_RANGE);
    const cached = initialQuery ? cachedRead("getOverviewMetrics", initialQuery) : undefined;
    return initialQuery && cached ? { key: metricsKey(scopeKey, initialQuery), metrics: cached } : null;
  });
  const [loading, setLoading] = React.useState(true);
  const [failure, setFailure] = React.useState<{ key: string; message: string } | null>(null);

  const rangeProblem: OverviewRangeProblem | null = overviewRangeProblem(period, range);
  const query = React.useMemo(() => overviewMetricsQuery(period, range), [period, range]);
  const key = query ? metricsKey(scopeKey, query) : null;
  const currentKey = React.useRef(key);
  currentKey.current = key;
  const requestSequence = React.useRef(0);
  const shown = answer?.key === key ? answer.metrics : null;
  const error = failure?.key === key ? failure.message : null;

  const load = React.useCallback(() => {
    const sequence = ++requestSequence.current;
    if (!query || !key) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const cached = cachedRead("getOverviewMetrics", query);
    if (cached) setAnswer({ key, metrics: cached });
    void cockpit
      .getOverviewMetrics(query)
      .then((next) => {
        if (sequence !== requestSequence.current || currentKey.current !== key) return;
        setAnswer({ key, metrics: next });
        setFailure(null);
      })
      .catch((e: unknown) => {
        if (sequence !== requestSequence.current || currentKey.current !== key) return;
        setFailure({ key, message: (e as { message?: string })?.message || t("overview.metrics.error") });
      })
      .finally(() => {
        if (sequence === requestSequence.current && currentKey.current === key) setLoading(false);
      });
  }, [key, query, t]);

  React.useEffect(() => { load(); }, [load, invalidations]);

  React.useEffect(() => {
    return () => { requestSequence.current += 1; };
  }, []);

  const state = overviewPanelState({ metrics: shown, loading, error, rangeProblem });
  const newReading = countReading(shown, (m) => m.new_enquiries, lang);
  const within24hReading = ratioReading(shown, lang);
  const delayedReading = countReading(shown, (m) => m.delayed_now, lang);
  const averageGuestReading = averageReading(shown, lang);
  const expectedRatioAbsence = shown?.processed_within_24h_ratio === null
    && shown.processed_within_24h_denominator === 0;
  const expectedAverageAbsence = shown?.average_guest_count === null
    && shown.average_guest_count_denominator === 0;

  const problemText = period === "custom" && !range.from && !range.to
    ? null
    : rangeProblem === "incomplete"
      ? t("overview.metrics.rangeIncomplete")
      // The other three say exactly what the Inbox says about the same range (#814); one message
      // per rule, not one per screen.
      : rangeProblem
      ? t(`inbox.dateRange.${rangeProblem}`)
      : null;

  return (
    <section
      className="ov-card ov-metrics"
      aria-label={t("overview.metrics.title")}
      data-testid="overview-metrics"
      data-state={state}
      aria-busy={state === "loading" ? true : undefined}
    >
      <div className="ov-card__head ov-metrics__head">
        <h2>{t("overview.metrics.title")}</h2>
        {shown && !rangeProblem && (
          // Hidden while the typed period is not yet a period: the last answer's dates sitting in
          // the header would read as a label for what the operator is now typing.
          <span className="ov-metrics__range" data-testid="overview-metrics-range">
            {periodRangeLabel(shown, lang)}
          </span>
        )}
        <span className="spacer" />
        <div className="ov-metrics__periods" role="group" aria-label={t("overview.metrics.periodAria")}>
          {OVERVIEW_PERIODS.map((option) => (
            <button
              key={option}
              type="button"
              className={`ov-metrics__period${option === period ? " is-active" : ""}`}
              aria-pressed={option === period}
              data-testid={`overview-period-${option}`}
              onClick={() => setPeriod(option)}
            >
              {t(`overview.metrics.period.${option}`)}
            </button>
          ))}
        </div>
      </div>

      {period === "custom" && (
        <div className="ov-metrics__range-inputs inbox-filter-range">
          <Input
            size="sm"
            type="date"
            value={range.from}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setRange((prev) => ({ ...prev, from: event.target.value }))
            }
            aria-label={t("inbox.filterDateFromAria")}
            aria-invalid={problemText ? true : undefined}
          />
          <span className="inbox-filter-range__dash" aria-hidden="true">–</span>
          <Input
            size="sm"
            type="date"
            value={range.to}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setRange((prev) => ({ ...prev, to: event.target.value }))
            }
            aria-label={t("inbox.filterDateToAria")}
            aria-invalid={problemText ? true : undefined}
          />
        </div>
      )}

      {problemText && (
        <p className="inbox-filter-problem" role="alert" data-testid="overview-metrics-problem">
          {problemText}
        </p>
      )}

      {state === "error" && (
        <p className="ov-metrics__error" role="alert" data-testid="overview-metrics-error">
          <Icon name="alert" size={16} />
          {t("overview.metrics.error")}{" "}
          <button type="button" className="linklike" onClick={load}>
            {t("common.retry")}
          </button>
        </p>
      )}

      {!rangeProblem && (
        <div className="ov-metrics__grid">
          <MetricRow
            id="new"
            icon="mail"
            label={t("overview.metrics.newEnquiries")}
            reading={newReading}
            hint={metricHint(
              newReading,
              t("overview.metrics.loading"),
              t("overview.metrics.selectedPeriod"),
              t("overview.metrics.unavailable"),
            )}
          />
          <MetricRow
            id="within24h"
            icon="clock"
            label={t("overview.metrics.within24h")}
            reading={within24hReading}
            hint={metricHint(
              within24hReading,
              t("overview.metrics.loading"),
              shown
                ? t("overview.metrics.within24hHint", {
                    n: shown.processed_within_24h,
                    total: shown.processed_within_24h_denominator,
                  })
                : t("overview.metrics.loading"),
              t(expectedRatioAbsence
                ? "overview.metrics.within24hUnavailable"
                : "overview.metrics.unavailable"),
            )}
          />
          <MetricRow
            id="delayed"
            icon="alert"
            label={t("overview.metrics.delayed")}
            reading={delayedReading}
            hint={metricHint(
              delayedReading,
              t("overview.metrics.loading"),
              t("overview.metrics.delayedHint"),
              t("overview.metrics.unavailable"),
            )}
          />
          <MetricRow
            id="guests"
            icon="users"
            label={t("overview.metrics.averageGuests")}
            reading={averageGuestReading}
            hint={metricHint(
              averageGuestReading,
              t("overview.metrics.loading"),
              shown
                ? t("overview.metrics.averageGuestsHint", {
                    n: shown.average_guest_count_denominator,
                  })
                : t("overview.metrics.loading"),
              t(expectedAverageAbsence
                ? shown?.new_enquiries === 0
                  ? "overview.metrics.periodEmpty"
                  : "overview.metrics.averageGuestsUnavailable"
                : "overview.metrics.unavailable"),
            )}
          />
        </div>
      )}

      {state === "loading" && <span className="sr-only">{t("overview.metrics.loading")}</span>}

      {shown && (
        <span className="sr-only">{t("overview.metrics.zoneNote", { zone: shown.time_zone })}</span>
      )}
    </section>
  );
}
